import { useMemo } from "react";
import type { RowData } from "@tanstack/react-table";
import { buildChangeHandlers, buildControlledState } from "./tableOptions";
import type { UseDataGridOptions } from "./types";

/**
 * The controlled slices' state and change handlers for TanStack. Keyed on
 * the controlled values and handlers, since option objects are usually
 * inline literals.
 */
export const useControlledSlices = <TData extends RowData>(
    options: UseDataGridOptions<TData>,
) => {
    const rowSelection = options.rowSelection?.state;
    const sorting = options.sorting?.state;
    const globalFilter = options.globalFilter?.state;
    const columnFilters = options.columnFilters?.state;
    const expanded = options.expanding?.state;
    const grouping = options.grouping?.state;
    const pagination = options.pagination?.state;
    const controlledState = useMemo(
        () =>
            buildControlledState({
                rowSelection,
                sorting,
                globalFilter,
                columnFilters,
                expanded,
                grouping,
                pagination,
            }),
        [
            rowSelection,
            sorting,
            globalFilter,
            columnFilters,
            expanded,
            grouping,
            pagination,
        ],
    );

    const onRowSelectionChange = options.rowSelection?.onChange;
    const onSortingChange = options.sorting?.onChange;
    const onGlobalFilterChange = options.globalFilter?.onChange;
    const onColumnFiltersChange = options.columnFilters?.onChange;
    const onExpandedChange = options.expanding?.onChange;
    const onGroupingChange = options.grouping?.onChange;
    const onPaginationChange = options.pagination?.onChange;
    const changeHandlers = useMemo(
        () =>
            buildChangeHandlers<TData>({
                onRowSelectionChange,
                onSortingChange,
                onGlobalFilterChange,
                onColumnFiltersChange,
                onExpandedChange,
                onGroupingChange,
                onPaginationChange,
            }),
        [
            onRowSelectionChange,
            onSortingChange,
            onGlobalFilterChange,
            onColumnFiltersChange,
            onExpandedChange,
            onGroupingChange,
            onPaginationChange,
        ],
    );
    return { controlledState, changeHandlers };
};
